import { Box, Container, Grid, Typography, Button, useTheme, Paper, Avatar } from "@mui/material";
import React, { useMemo } from "react";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Seo from "../../seo/Seo";
import { SOFTWARE_JSONLD, ORGANIZATION_JSONLD } from "../../seo/seoConfig";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import TimelineIcon from "@mui/icons-material/Timeline";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SendIcon from "@mui/icons-material/Send";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import StarIcon from "@mui/icons-material/Star";
import PersonIcon from "@mui/icons-material/Person";

export default function Marketing({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { t } = useTranslation('marketing');
  const { t: tSeo } = useTranslation('seo');

  // Global styles for animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0% {
          transform: translateY(0px) rotate(0deg);
        }
        50% {
          transform: translateY(-20px) rotate(2deg);
        }
        100% {
          transform: translateY(0px) rotate(0deg);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const features = useMemo(() => [
    {
      icon: MonitorHeartIcon,
      title: t('features.automatedFollowups.title'),
      description: t('features.automatedFollowups.description'),
    },
    {
      icon: PsychologyIcon,
      title: t('features.aiAnalytics.title'),
      description: t('features.aiAnalytics.description'),
    },
    {
      icon: TimelineIcon,
      title: t('features.prospectiveStudies.title'),
      description: t('features.prospectiveStudies.description'),
    },
    {
      icon: AnalyticsIcon,
      title: t('features.flexibleOutput.title'),
      description: t('features.flexibleOutput.description'),
    },
  ], [t]);

  const benefits = useMemo(() => [
    {
      title: t('benefits.reliableData.title'),
      description: t('benefits.reliableData.description'),
      icon: AssessmentIcon,
    },
    {
      title: t('benefits.researchExcellence.title'),
      description: t('benefits.researchExcellence.description'),
      icon: MonitorHeartIcon,
    },
    {
      title: t('benefits.statisticalConvenience.title'),
      description: t('benefits.statisticalConvenience.description'),
      icon: TimelineIcon,
    },
  ], [t]);

  const testimonials = useMemo(() => [
    {
      comment: t('testimonials.testimonial1.comment'),
      name: t('testimonials.testimonial1.name'),
      title: t('testimonials.testimonial1.title'),
    },
    {
      comment: t('testimonials.testimonial2.comment'),
      name: t('testimonials.testimonial2.name'),
      title: t('testimonials.testimonial2.title'),
    },
    {
      comment: t('testimonials.testimonial3.comment'),
      name: t('testimonials.testimonial3.name'),
      title: t('testimonials.testimonial3.title'),
    },
  ], [t]);

  const integrations = useMemo(() => [
    {
      name: t('integrations.hospitalSystems'),
      icon: LocalHospitalIcon,
    },
    {
      name: t('integrations.fileCreation'),
      icon: FolderOpenIcon,
    },
    {
      name: t('integrations.formCreation'),
      icon: DescriptionOutlinedIcon,
    },
    {
      name: t('integrations.formSending'),
      icon: SendIcon,
    },
    {
      name: t('integrations.patientAddition'),
      icon: PersonAddIcon,
    },
    {
      name: t('integrations.mobileApps'),
      icon: PhoneIphoneIcon,
    },
    {
      name: t('integrations.calendarSystems'),
      icon: CalendarMonthIcon,
    },
    {
      name: t('integrations.dataAnalysis'),
      icon: AssessmentIcon,
    },
  ], [t]);

  const FloatingCard = ({ icon: Icon, title, description, style, rating = 5 }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: [0, -15, 0],
        x: [-5, 5, -5],
        transition: {
          y: {
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          },
          x: {
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }
        }
      }}
      style={{
        position: "absolute",
        ...style,
      }}
    >
      <Box
        sx={{
          p: 4,
          width: "300px",
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Icon sx={{ fontSize: 28, color: colors.greenAccent[400] }} />
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 600,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.8)",
            lineHeight: 1.8,
            mb: 2,
          }}
        >
          {description}
        </Typography>
        <Box display="flex" gap={0.5}>
          {[...Array(5)].map((_, index) => (
            <StarIcon
              key={index}
              sx={{
                fontSize: 20,
                color: index < rating ? colors.greenAccent[400] : "rgba(255, 255, 255, 0.2)",
              }}
            />
          ))}
        </Box>
      </Box>
    </motion.div>
  );

  const StatCard = ({ number, text, style }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: [0, -15, 0],
        x: [-5, 5, -5],
        transition: {
          y: {
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          },
          x: {
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }
        }
      }}
      style={{
        position: "absolute",
        ...style,
      }}
    >
      <Box
        sx={{
          p: 3,
          width: "200px",
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Typography
          variant="h3"
          sx={{
            color: "white",
            fontWeight: 700,
            mb: 1,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {number}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.8)",
            lineHeight: 1.6,
          }}
        >
          {text}
        </Typography>
      </Box>
    </motion.div>
  );

  const FeatureCard = ({ icon: Icon, title, description }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      style={{ height: "100%" }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          height: "100%",
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)",
            opacity: 0,
            transition: "opacity 0.3s ease",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            background: "linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)",
            transform: "rotate(45deg)",
            transition: "all 0.8s ease",
            opacity: 0,
          },
          "&:hover": {
            transform: "translateY(-10px)",
            "&::before": {
              opacity: 1,
            },
            "&::after": {
              opacity: 1,
              left: "100%",
              top: "100%",
            },
            "& .icon-container": {
              transform: "scale(1.1) rotate(5deg)",
              background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
            },
          },
        }}
      >
        <Box
          className="icon-container"
          sx={{
            width: 60,
            height: 60,
            borderRadius: "15px",
            background: "rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            transition: "all 0.3s ease",
          }}
        >
          <Icon
            sx={{
              fontSize: 32,
              color: colors.greenAccent[400],
            }}
          />
        </Box>
        <Typography
          variant="h5"
          sx={{
            color: "white",
            fontWeight: 600,
            mb: 2,
            fontFamily: "'Poppins', sans-serif",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: 0,
              width: 40,
              height: 3,
              background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
              borderRadius: "2px",
            },
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.8)",
            lineHeight: 1.6,
            flex: 1,
          }}
        >
          {description}
        </Typography>
      </Paper>
    </motion.div>
  );

  const BenefitCard = ({ icon: Icon, title, description }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      style={{ height: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 3,
          p: 3,
          height: "100%",
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -100,
            right: -100,
            width: 200,
            height: 200,
            background: `radial-gradient(circle, ${colors.greenAccent[500]}20, transparent 70%)`,
            transition: "all 0.3s ease",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            background: "linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)",
            transform: "rotate(45deg)",
            transition: "all 0.8s ease",
            opacity: 0,
          },
          "&:hover": {
            transform: "translateY(-5px)",
            "&::before": {
              transform: "scale(1.2)",
            },
            "&::after": {
              opacity: 1,
              left: "100%",
              top: "100%",
            },
            "& .benefit-icon": {
              transform: "rotate(10deg) scale(1.1)",
              color: colors.greenAccent[300],
            },
          },
        }}
      >
        <Icon
          className="benefit-icon"
          sx={{
            fontSize: 40,
            color: colors.greenAccent[400],
            mt: 1,
            transition: "all 0.3s ease",
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 600,
              mb: 1,
              fontFamily: "'Poppins', sans-serif",
              position: "relative",
              display: "inline-block",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -4,
                left: 0,
                width: "0%",
                height: 2,
                background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
                transition: "width 0.3s ease",
              },
              "&:hover::after": {
                width: "100%",
              },
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              lineHeight: 1.6,
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );

  return (
    <Box sx={{ 
      width: "100%",
      background: darkMode ? colors.primary[600] : 'white',
      color: darkMode ? 'white' : colors.grey[100],
    }}>
      <Seo
        path="/"
        title={tSeo("home.title")}
        description={tSeo("home.description")}
        keywords={tSeo("home.keywords")}
        jsonLd={[ORGANIZATION_JSONLD, SOFTWARE_JSONLD]}
      />
      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />
      
      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode 
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Hero Section */}
        <Container maxWidth="lg" sx={{ pt: 15, pb: 10 }}>
          <Grid container spacing={6} alignItems="center" sx={{ position: "relative", zIndex: 2 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Box>
                  <Typography
                    variant="h1"
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      mb: 3,
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    {t('hero.title')}
                    <br />
                    <span
                      style={{
                        background: `linear-gradient(135deg, ${colors.greenAccent[300]}, ${colors.blueAccent[200]})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {t('hero.titleHighlight')}
                    </span>
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      mb: 4,
                      fontWeight: 300,
                      lineHeight: 1.6,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {t('hero.description')}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate("/tenantRegister")}
                      sx={{
                        backgroundColor: "white",
                        color: colors.greenAccent[500],
                        padding: "12px 32px",
                        borderRadius: "30px",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.9)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      {t('hero.getStarted')}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate("/about")}
                      sx={{
                        borderColor: "white",
                        color: "white",
                        padding: "12px 32px",
                        borderRadius: "30px",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          borderColor: "rgba(255,255,255,0.9)",
                          backgroundColor: "rgba(255,255,255,0.1)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {t('hero.learnMore')}
                    </Button>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "10%",
                      left: "10%",
                      width: "80%",
                      height: "80%",
                      background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
                      borderRadius: "30px",
                      filter: "blur(25px)",
                      opacity: 0.3,
                      zIndex: 0,
                    },
                  }}
                >
                  <img
                    src="../assets/marketing-5.png"
                    alt={`${t('hero.title')} ${t('hero.titleHighlight')} - ${t('hero.description')}`}
                    loading="eager"
                    decoding="async"
                    width="600"
                    height="400"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxWidth: "600px",
                      borderRadius: "20px",
                      position: "relative",
                      zIndex: 1,
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Features Section */}
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              sx={{
                color: "white",
                textAlign: "center",
                mb: 2,
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {t('features.title')}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                textAlign: "center",
                mb: 8,
                maxWidth: "600px",
                mx: "auto",
                fontSize: "1.1rem",
                lineHeight: 1.6,
              }}
            >
              {t('features.subtitle')}
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <FeatureCard {...feature} />
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Benefits Section */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.03)",
            backdropFilter: "blur(10px)",
            py: 10,
            mt: 8,
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: "white",
                  textAlign: "center",
                  mb: 2,
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                {t('benefits.title')}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  textAlign: "center",
                  mb: 8,
                  maxWidth: "600px",
                  mx: "auto",
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                }}
              >
                {t('benefits.subtitle')}
              </Typography>
            </motion.div>

            <Grid container spacing={4}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <BenefitCard {...benefit} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Testimonials Section */}
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              sx={{
                color: "white",
                textAlign: "center",
                mb: 2,
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {t('testimonials.title')}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                textAlign: "center",
                mb: 8,
                maxWidth: "600px",
                mx: "auto",
                fontSize: "1.1rem",
                lineHeight: 1.6,
              }}
            >
              {t('testimonials.subtitle')}
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: "20px",
                      background: "rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      height: "100%",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-10px)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: "rgba(255, 255, 255, 0.9)",
                        mb: 4,
                        fontSize: "1.1rem",
                        lineHeight: 1.6,
                        fontStyle: "italic",
                      }}
                    >
                      "{testimonial.comment}"
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: colors.greenAccent[500],
                        }}
                      >
                        {testimonial.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            color: "white",
                            fontWeight: 600,
                            fontSize: "1.1rem",
                          }}
                        >
                          {testimonial.name}
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.greenAccent[400],
                            fontSize: "0.9rem",
                          }}
                        >
                          {testimonial.title}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Integration Section */}
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              sx={{
                color: "white",
                textAlign: "center",
                mb: 2,
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {t('integrations.title')}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                textAlign: "center",
                mb: 8,
                maxWidth: "600px",
                mx: "auto",
                fontSize: "1.1rem",
                lineHeight: 1.6,
              }}
            >
              {t('integrations.subtitle')}
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {integrations.map((integration, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      background: "rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        background: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <integration.icon sx={{ fontSize: 40, color: colors.greenAccent[400] }} />
                    <Typography
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        textAlign: "center",
                      }}
                    >
                      {integration.name}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* CTA Section */}
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                textAlign: "center",
                p: 6,
                borderRadius: "30px",
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <HealthAndSafetyIcon
                sx={{
                  fontSize: 64,
                  color: colors.greenAccent[400],
                  mb: 3,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  mb: 2,
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                {t('cta.title')}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  mb: 4,
                  maxWidth: "600px",
                  mx: "auto",
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                }}
              >
                {t('cta.description')}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/tenantRegister")}
                sx={{
                  backgroundColor: "white",
                  color: colors.greenAccent[500],
                  padding: "12px 32px",
                  borderRadius: "30px",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.9)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {t('cta.getStarted')}
              </Button>
            </Box>
          </motion.div>
        </Container>

        <Footer />
      </Box>
    </Box>
  );
}
